import { annexureAPI, vmsOutsourcingVendorsAPI, vmsRegisteredVendorsAPI } from '../../services/api';
import { parseVmsDate } from '../../utils/vmsDateFormat';
import { approverPickerContextForAnnexureCode, loadVmsApproverOptions } from '../../utils/vmsApprovers';
import { normalizeAnnexureDefinitionPayload } from '../../utils/annexureQuestionnaireStructure';
import type { AnnexurePickerUser } from '../../components/vms/AnnexureBusinessHeadPicker';
import type { AnnexureDefinitionPayload } from '../../types';
import type { AnnexureApproverFeedbackItem } from '../../types/annexureApproverFeedback';
import type { OngoingVendorReviewPeriodicAnnexureId } from './ongoingVendorReviewAnnexureForms';
import {
  MONTHLY_ANNEXURE_11_ROW_IDS,
  MONTHLY_PERFORMANCE_INDICATORS,
} from './ongoingVendorReviewViewsModel';

export type OngoingVendorReviewAssessmentType = 'monthly' | 'periodic';
export type OngoingVendorReviewAnnexureCode = '6' | '7' | '8' | '10' | '11' | '12' | '13';
export type OngoingVendorReviewApplicability = 'applicable' | 'not_applicable';
export type OngoingVendorReviewScope =
  | { kind: 'outsourcing'; id: number }
  | { kind: 'registered'; id: number; outsourcingVendorId?: number | null };

export type OngoingVendorReviewBatchRow = {
  id: number;
  assessment_type: OngoingVendorReviewAssessmentType | string;
  status: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type OngoingVendorReviewAnnexureApprover = {
  id: number;
  approver_id: number;
  status: string;
  approved_at?: string | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  approver_vms_role?: string | null;
};

export function formatApproverVmsRoleLabel(role: string | null | undefined): string {
  const r = String(role ?? '').trim().toLowerCase();
  if (!r) return 'approver';
  if (r === 'business_head') return 'business head';
  if (r === 'dpo' || r === 'dpia_officer') return 'DPO';
  if (r === 'legal') return 'legal';
  return r.replace(/_/g, ' ');
}

export type OngoingVendorReviewAnnexureItem = {
  id?: number;
  annexure_code?: string;
  applicability?: OngoingVendorReviewApplicability | string | null;
  status?: string | null;
  response?: Record<string, unknown> | string | null;
  configured_approver_ids?: number[];
  /** Parallel BH votes while pending_approval / after approved. */
  approvers?: OngoingVendorReviewAnnexureApprover[];
  /** Optional approver notes + files from approve / need-more-information actions. */
  approver_feedback?: AnnexureApproverFeedbackItem[];
  needs_more_info_comment?: string | null;
  /** Admin reason when applicability is not_applicable (onboarding hub). */
  not_applicable_reason?: string | null;
  updated_at?: string | null;
};

/** Show per-approver Approved / Pending list (not the editable picker). */
export function showOngoingAnnexureApproverProgress(
  item: OngoingVendorReviewAnnexureItem | null | undefined
): boolean {
  const st = String(item?.status ?? '').toLowerCase();
  if (st !== 'pending_approval' && st !== 'approved') return false;
  return (item?.approvers?.length ?? 0) > 0;
}

export type OngoingVendorReviewAnnexureRowDto = {
  code: string;
  title: string;
  item: OngoingVendorReviewAnnexureItem | null;
  /** Resolved (pinned or latest) from batch annexures API — use for Excel import/export. */
  definition?: AnnexureDefinitionPayload | Record<string, unknown> | null;
  annexure_definition_version_id?: number | null;
  definition_is_pinned?: boolean;
};

function normalizeBatchDefinitionPayload(raw: unknown): AnnexureDefinitionPayload | null {
  return normalizeAnnexureDefinitionPayload(raw);
}

/** Version-aware definition from `loadBatchAnnexures` (preferred over bare `getByCode`). */
export function definitionFromOngoingBatchAnnexureRow(
  annexures: OngoingVendorReviewAnnexureRowDto[] | undefined,
  code: string
): AnnexureDefinitionPayload | null {
  const row = (annexures ?? []).find((a) => String(a.code) === String(code));
  return normalizeBatchDefinitionPayload(row?.definition);
}

/** Query params for versioned `getByCode` when a registered vendor backs the batch. */
export function ongoingAnnexureExcelGetByCodeParams(
  vendorScope: OngoingVendorReviewScope,
  batchId: number
): { vms_registered_vendor_id: number; ongoing_batch_id: number } | null {
  if (vendorScope.kind === 'registered' && vendorScope.id > 0 && batchId > 0) {
    return { vms_registered_vendor_id: vendorScope.id, ongoing_batch_id: batchId };
  }
  return null;
}

/** Load annexure definition for Excel: batch list first, then contextual `getByCode`. */
export async function loadOngoingAnnexureDefinitionForExcel(
  vendorScope: OngoingVendorReviewScope,
  batchId: number,
  code: string,
  annexuresFromBatch?: OngoingVendorReviewAnnexureRowDto[]
): Promise<AnnexureDefinitionPayload> {
  const fromBatch = definitionFromOngoingBatchAnnexureRow(annexuresFromBatch, code);
  if (fromBatch) return fromBatch;
  const params = ongoingAnnexureExcelGetByCodeParams(vendorScope, batchId);
  const payload = params
    ? await annexureAPI.getByCode(code, params)
    : await annexureAPI.getByCode(code);
  const def = payload.definition?.definition;
  const normalized = normalizeBatchDefinitionPayload(def);
  if (!normalized) throw new Error('Annexure definition is missing');
  return normalized;
}

export type OngoingVendorReviewBatchAnnexuresPayload = {
  vendor_display_name?: string;
  vendor_initiation_id?: number | null;
  assessment_type?: OngoingVendorReviewAssessmentType | string;
  batch_status?: string;
  annexures?: OngoingVendorReviewAnnexureRowDto[];
  pending_approver_codes_for_me?: string[];
};

function isOutsourcingScope(scope: OngoingVendorReviewScope): scope is Extract<OngoingVendorReviewScope, { kind: 'outsourcing' }> {
  return scope.kind === 'outsourcing';
}

export const PERIODIC_ANNEXURE_ID_TO_CODE: Record<OngoingVendorReviewPeriodicAnnexureId, Exclude<OngoingVendorReviewAnnexureCode, '11'>> = {
  a6: '6',
  a7: '7',
  a8: '8',
  a10: '10',
  a12: '12',
  a13: '13',
};

export const PERIODIC_CODE_TO_ANNEXURE_ID: Partial<Record<OngoingVendorReviewAnnexureCode, OngoingVendorReviewPeriodicAnnexureId>> = {
  '6': 'a6',
  '7': 'a7',
  '8': 'a8',
  '10': 'a10',
  '12': 'a12',
  '13': 'a13',
};

const ANNEXURE_7_ROW_IDS = ['fr1', 'fr2', 'op1', 'op2', 'op3', 'op4', 'rep1', 'comp1', 'it1', 'it2', 'dr1'];
const ANNEXURE_8_ROW_IDS = [
  'g1r1',
  'g1r2',
  'g1r3',
  'g2r1',
  'g2r2',
  'g2r3',
  'g3r1',
  'g3r2',
  'g4r1',
  'g4r2',
  'g5r1',
  'g5r2',
  'g6r1',
  'g6r2',
  'g6r3',
  'g7r1',
];

export function normalizePickerUsers(res: unknown): AnnexurePickerUser[] {
  if (Array.isArray(res)) return res as AnnexurePickerUser[];
  if (res && typeof res === 'object') {
    const o = res as Record<string, unknown>;
    if (o.success === true && Array.isArray(o.data)) return o.data as AnnexurePickerUser[];
  }
  return [];
}

export async function loadBusinessHeadUsers(
  annexureCode?: string,
): Promise<AnnexurePickerUser[]> {
  const context =
    annexureCode != null
      ? approverPickerContextForAnnexureCode(annexureCode, 'ongoing_annexure')
      : 'ongoing_annexure';
  const list = await loadVmsApproverOptions(context);
  return list as AnnexurePickerUser[];
}

export async function loadBatchAnnexures(
  vendorScope: OngoingVendorReviewScope,
  batchId: number
): Promise<OngoingVendorReviewBatchAnnexuresPayload> {
  return (isOutsourcingScope(vendorScope)
    ? await vmsOutsourcingVendorsAPI.getOngoingAnnexuresForBatch(vendorScope.id, batchId)
    : await vmsRegisteredVendorsAPI.getOngoingAnnexuresForBatch(vendorScope.id, batchId)) as OngoingVendorReviewBatchAnnexuresPayload;
}

export async function listOngoingVendorReviewBatches(
  vendorScope: OngoingVendorReviewScope,
  assessmentType: OngoingVendorReviewAssessmentType
): Promise<OngoingVendorReviewBatchRow[]> {
  const res = (isOutsourcingScope(vendorScope)
    ? await vmsOutsourcingVendorsAPI.listOngoingAnnexureBatches(vendorScope.id, {
    assessment_type: assessmentType,
      })
    : await vmsRegisteredVendorsAPI.listOngoingAnnexureBatches(vendorScope.id, {
        assessment_type: assessmentType,
      })) as { success?: boolean; data?: OngoingVendorReviewBatchRow[] };
  return res.success !== false && Array.isArray(res.data) ? res.data : [];
}

export async function createOngoingVendorReviewBatch(
  vendorScope: OngoingVendorReviewScope,
  assessmentType: OngoingVendorReviewAssessmentType
): Promise<number> {
  const res = (isOutsourcingScope(vendorScope)
    ? await vmsOutsourcingVendorsAPI.createOngoingAnnexureBatch(vendorScope.id, {
        assessment_type: assessmentType,
      })
    : await vmsRegisteredVendorsAPI.createOngoingAnnexureBatch(vendorScope.id, {
        assessment_type: assessmentType,
        ...(vendorScope.outsourcingVendorId != null ? { vms_outsourcing_vendor_id: vendorScope.outsourcingVendorId } : {}),
      })) as { success?: boolean; data?: { id?: number; existing_batch_id?: number }; message?: string };
  const id = Number(res.data?.id ?? res.data?.existing_batch_id);
  if (!res.success || !Number.isInteger(id) || id < 1) {
    throw new Error(res.message ?? 'Could not start review');
  }
  return id;
}

/** Ensures a draft batch item exists (applicable + approvers) so uploads and saves can proceed. */
export async function ensureOngoingVendorReviewAnnexureConfigured(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
  approverIds?: number[];
}): Promise<void> {
  await saveOngoingVendorReviewBatchConfig({
    vendorScope: args.vendorScope,
    batchId: args.batchId,
    items: [
      {
        code: args.code,
        applicability: 'applicable',
        approverIds: args.approverIds ?? [],
      },
    ],
  });
}

export async function saveOngoingVendorReviewBatchConfig(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  items: { code: OngoingVendorReviewAnnexureCode; applicability: OngoingVendorReviewApplicability; approverIds?: number[] }[];
}): Promise<void> {
  const items = args.items.map((item) => ({
      code: item.code,
      applicability: item.applicability,
      approver_ids: item.approverIds ?? [],
    }));
  if (isOutsourcingScope(args.vendorScope)) {
    await vmsOutsourcingVendorsAPI.patchOngoingAnnexureBatchConfig(args.vendorScope.id, args.batchId, items);
  } else {
    await vmsRegisteredVendorsAPI.patchOngoingAnnexureBatchConfig(args.vendorScope.id, args.batchId, items);
  }
}

/** Persist approver assignment only (e.g. while pending before any approver acts). */
export async function saveOngoingVendorReviewApproversOnly(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
  approverIds: number[];
  applicability?: OngoingVendorReviewApplicability;
}): Promise<void> {
  await saveOngoingVendorReviewBatchConfig({
    vendorScope: args.vendorScope,
    batchId: args.batchId,
    items: [
      {
        code: args.code,
        applicability: args.applicability ?? 'applicable',
        approverIds: args.approverIds,
      },
    ],
  });
}

export async function saveOngoingVendorReviewAnnexure(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
  response: Record<string, unknown>;
  approverIds: number[];
  applicability?: OngoingVendorReviewApplicability;
}): Promise<void> {
  const applicability = args.applicability ?? 'applicable';
  const items = [{ code: args.code, applicability, approver_ids: args.approverIds }];
  if (isOutsourcingScope(args.vendorScope)) {
    await vmsOutsourcingVendorsAPI.patchOngoingAnnexureBatchConfig(args.vendorScope.id, args.batchId, items);
    await vmsOutsourcingVendorsAPI.putOngoingAnnexureBatchResponse(
      args.vendorScope.id,
      args.batchId,
      args.code,
      args.response
    );
  } else {
    await vmsRegisteredVendorsAPI.patchOngoingAnnexureBatchConfig(args.vendorScope.id, args.batchId, items);
    await vmsRegisteredVendorsAPI.putOngoingAnnexureBatchResponse(
      args.vendorScope.id,
      args.batchId,
      args.code,
      args.response
    );
  }
}

export async function submitOngoingVendorReviewAnnexure(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
}): Promise<void> {
  if (isOutsourcingScope(args.vendorScope)) {
    await vmsOutsourcingVendorsAPI.submitOngoingAnnexureBatchAnnexure(args.vendorScope.id, args.batchId, args.code);
  } else {
    await vmsRegisteredVendorsAPI.submitOngoingAnnexureBatchAnnexure(args.vendorScope.id, args.batchId, args.code);
  }
}

export async function resubmitOngoingVendorReviewAnnexure(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
}): Promise<void> {
  if (isOutsourcingScope(args.vendorScope)) {
    await vmsOutsourcingVendorsAPI.resubmitOngoingAnnexureBatchAnnexure(args.vendorScope.id, args.batchId, args.code);
  } else {
    await vmsRegisteredVendorsAPI.resubmitOngoingAnnexureBatchAnnexure(args.vendorScope.id, args.batchId, args.code);
  }
}

export async function approveOngoingVendorReviewAnnexure(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
  approverComment?: string;
  approverAttachments?: File[];
}): Promise<void> {
  const extras = {
    approverComment: args.approverComment,
    approverAttachments: args.approverAttachments,
  };
  if (isOutsourcingScope(args.vendorScope)) {
    await vmsOutsourcingVendorsAPI.approveOngoingAnnexureBatchAnnexure(
      args.vendorScope.id,
      args.batchId,
      args.code,
      extras
    );
  } else {
    await vmsRegisteredVendorsAPI.approveOngoingAnnexureBatchAnnexure(
      args.vendorScope.id,
      args.batchId,
      args.code,
      extras
    );
  }
}

export async function needMoreInfoOngoingVendorReviewAnnexure(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
  comment: string;
  approverComment?: string;
  approverAttachments?: File[];
}): Promise<void> {
  const body = {
    comment: args.comment,
    approverComment: args.approverComment,
    approverAttachments: args.approverAttachments,
  };
  if (isOutsourcingScope(args.vendorScope)) {
    await vmsOutsourcingVendorsAPI.needMoreInfoOngoingAnnexureBatchAnnexure(
      args.vendorScope.id,
      args.batchId,
      args.code,
      body
    );
  } else {
    await vmsRegisteredVendorsAPI.needMoreInfoOngoingAnnexureBatchAnnexure(
      args.vendorScope.id,
      args.batchId,
      args.code,
      body
    );
  }
}

export async function listOngoingVendorReviewRowComments(
  vendorScope: OngoingVendorReviewScope,
  batchId: number,
  code: OngoingVendorReviewAnnexureCode
) {
  return isOutsourcingScope(vendorScope)
    ? vmsOutsourcingVendorsAPI.listOngoingAnnexureRowComments(vendorScope.id, batchId, code)
    : vmsRegisteredVendorsAPI.listOngoingAnnexureRowComments(vendorScope.id, batchId, code);
}

export async function upsertOngoingVendorReviewRowComment(args: {
  vendorScope: OngoingVendorReviewScope;
  batchId: number;
  code: OngoingVendorReviewAnnexureCode;
  rowId: string;
  comment: string;
}) {
  if (isOutsourcingScope(args.vendorScope)) {
    await vmsOutsourcingVendorsAPI.upsertOngoingAnnexureRowComment(args.vendorScope.id, args.batchId, args.code, {
      rowId: args.rowId,
      comment: args.comment,
    });
  } else {
    await vmsRegisteredVendorsAPI.upsertOngoingAnnexureRowComment(args.vendorScope.id, args.batchId, args.code, {
      rowId: args.rowId,
      comment: args.comment,
    });
  }
}

export function responseObject(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
}

export function isCompleteBatch(batch: OngoingVendorReviewBatchRow): boolean {
  return String(batch.status ?? '').trim().toLowerCase() === 'complete';
}

function batchUpdatedTime(batch: OngoingVendorReviewBatchRow): number {
  const raw = batch.updated_at ?? batch.created_at;
  if (!raw) return 0;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Monthly (Annexure 11) batch list label — e.g. "Performance Review - November 2026"
 * for the calendar month before the batch updated/created date.
 */
export function performanceReviewBatchLabel(batch: OngoingVendorReviewBatchRow): string {
  const anchor = batch.updated_at ?? batch.created_at;
  const parsed = parseVmsDate(anchor);
  if (!parsed) return `Performance Review - Review #${batch.id}`;
  const previousMonth = new Date(parsed.getFullYear(), parsed.getMonth() - 1, 1);
  const monthYear = previousMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  return `Performance Review - ${monthYear}`;
}

/** Matches vendor reviews table periodic due-date scheduling (day-of-month). */
export const PERIODIC_REVIEW_DUE_DAY = 1;

function periodicReviewBaselineForBatch(
  batch: OngoingVendorReviewBatchRow,
  allBatches: OngoingVendorReviewBatchRow[],
  registerPeriodicReviewYmd?: string | null
): Date | null {
  if (isCompleteBatch(batch)) {
    return parseVmsDate(batch.updated_at ?? batch.created_at);
  }

  const batchTime = batchUpdatedTime(batch);
  const priorComplete = [...allBatches]
    .filter((b) => isCompleteBatch(b) && b.id !== batch.id && batchUpdatedTime(b) < batchTime)
    .sort((a, b) => batchUpdatedTime(b) - batchUpdatedTime(a))[0];
  const priorCompleteDate = priorComplete
    ? parseVmsDate(priorComplete.updated_at ?? priorComplete.created_at)
    : null;
  const registerDate = registerPeriodicReviewYmd?.trim()
    ? parseVmsDate(registerPeriodicReviewYmd.trim())
    : null;

  if (priorCompleteDate && registerDate) {
    return priorCompleteDate.getTime() >= registerDate.getTime() ? priorCompleteDate : registerDate;
  }
  return (
    priorCompleteDate ??
    registerDate ??
    parseVmsDate(batch.created_at ?? batch.updated_at)
  );
}

/**
 * Periodic batch list label — e.g. "Periodic Review - May 2028" from the next review
 * due month/year (material: +12 months, non-material: +24 months from baseline).
 */
export function periodicReviewBatchLabel(
  batch: OngoingVendorReviewBatchRow,
  isMaterial: boolean,
  allPeriodicBatches: OngoingVendorReviewBatchRow[],
  registerPeriodicReviewYmd?: string | null
): string {
  const baseline = periodicReviewBaselineForBatch(batch, allPeriodicBatches, registerPeriodicReviewYmd);
  if (!baseline) return `Periodic Review - Review #${batch.id}`;
  const intervalMonths = isMaterial ? 12 : 24;
  const dueDate = new Date(
    baseline.getFullYear(),
    baseline.getMonth() + intervalMonths,
    PERIODIC_REVIEW_DUE_DAY
  );
  const monthYear = dueDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  return `Periodic Review - ${monthYear}`;
}

export function latestOngoingVendorReviewBatch(batches: OngoingVendorReviewBatchRow[]): OngoingVendorReviewBatchRow | null {
  return [...batches].sort((a, b) => {
    const aTime = batchUpdatedTime(a);
    const bTime = batchUpdatedTime(b);
    if (aTime !== bTime) return bTime - aTime;
    return Number(b.id ?? 0) - Number(a.id ?? 0);
  })[0] ?? null;
}

function sortOngoingVendorReviewBatches(
  batches: OngoingVendorReviewBatchRow[]
): OngoingVendorReviewBatchRow[] {
  return [...batches].sort((a, b) => {
    const aTime = batchUpdatedTime(a);
    const bTime = batchUpdatedTime(b);
    if (aTime !== bTime) return bTime - aTime;
    return Number(b.id ?? 0) - Number(a.id ?? 0);
  });
}

/**
 * Full monthly/periodic review history for the Reviews list (newest first).
 * Complete rows stay view-only when opened; this only controls list visibility.
 */
export function ongoingVendorReviewHistoryBatches(
  batches: OngoingVendorReviewBatchRow[]
): OngoingVendorReviewBatchRow[] {
  return sortOngoingVendorReviewBatches(batches);
}

/** Latest in-progress + latest complete — used for default selection / start-review gating helpers. */
export function visibleOngoingVendorReviewBatches(batches: OngoingVendorReviewBatchRow[]): OngoingVendorReviewBatchRow[] {
  const sorted = sortOngoingVendorReviewBatches(batches);
  const latestOpen = sorted.find((batch) => !isCompleteBatch(batch));
  const latestComplete = sorted.find(isCompleteBatch);
  return [latestOpen, latestComplete].filter(
    (batch, index, visible): batch is OngoingVendorReviewBatchRow =>
      batch != null && visible.findIndex((candidate) => candidate?.id === batch.id) === index
  );
}

/** Prefer the in-progress review; otherwise open the latest completed review for view-only. */
export function defaultOngoingVendorReviewBatchId(batches: OngoingVendorReviewBatchRow[]): number | null {
  const visible = visibleOngoingVendorReviewBatches(batches);
  const latestOpen = visible.find((batch) => !isCompleteBatch(batch));
  if (latestOpen) return latestOpen.id;
  const latestComplete = visible.find(isCompleteBatch);
  return latestComplete?.id ?? null;
}

export function isReadOnlyAnnexure(item: OngoingVendorReviewAnnexureItem | null | undefined, batchStatus?: string | null): boolean {
  if (String(batchStatus ?? '').toLowerCase() === 'complete') return true;
  const status = String(item?.status ?? 'draft').toLowerCase();
  return status !== 'draft' && status !== 'needs_more_information';
}

export function itemStatusLabel(item: OngoingVendorReviewAnnexureItem | null | undefined): string {
  return String(item?.status ?? 'draft').replace(/_/g, ' ');
}

export function statusToPeriodicVisual(
  item: OngoingVendorReviewAnnexureItem | null | undefined
): 'completed' | 'current' | 'pending' | 'notApplicable' {
  const status = String(item?.status ?? 'draft').toLowerCase();
  if (status === 'approved') return 'completed';
  return 'pending';
}

export function getResponseValue(response: Record<string, unknown>, key: string): string {
  const v = response[key];
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

export function patchResponseValue(
  response: Record<string, unknown>,
  key: string,
  value: string
): Record<string, unknown> {
  return { ...response, [key]: value };
}

export function periodicQuestionRowId(questionId: string): string {
  const match = questionId.match(/^a(\d+)-(\d+)$/);
  if (!match) return questionId;
  const code = match[1];
  const ordinal = Number(match[2]);
  if (code === '7') return ANNEXURE_7_ROW_IDS[ordinal - 1] ?? questionId;
  if (code === '8') return ANNEXURE_8_ROW_IDS[ordinal - 1] ?? questionId;
  return `n${code}_${ordinal}`;
}

export function periodicResponseKey(questionId: string, columnId: string): string {
  return `${periodicQuestionRowId(questionId)}_${columnId}`;
}

export function periodicJustificationColumnId(questionId: string): string {
  return questionId.startsWith('a8-') ? 'potential_harm' : 'justification';
}

export function periodicRatingColumnId(questionId: string): string {
  return questionId.startsWith('a8-') ? 'perceived_risk_level' : 'score';
}

/** @deprecated Prefer {@link monthlyAnnexure11RowIdForCard} + {@link getMonthlyAnnexure11FieldValue}. */
export function monthlyResponseKey(index: number, columnId: string): string {
  return `${monthlyAnnexure11RowIdForCard(index)}_${columnId}`;
}

/** 1-based card index on the monthly performance grid. */
export function monthlyAnnexure11RowIdForCard(cardIndex: number): string {
  const rowId = MONTHLY_ANNEXURE_11_ROW_IDS[cardIndex - 1];
  return rowId ?? `n11_new_${cardIndex}`;
}

const MONTHLY_ANNEXURE_11_OBSERVATION_COLUMN_IDS = [
  'observations',
  'assessment',
  'kyc_applicable',
  'sample_checking',
  'overall_performance',
  'action_taken',
] as const;

const MONTHLY_ANNEXURE_11_REMARKS_COLUMN_IDS = ['remarks_status', 'action_taken'] as const;

/** Last monthly card: Overall Performance Outcome. */
export const MONTHLY_ANNEXURE_11_OVERALL_OUTCOME_ROW_ID = 'n11_new_18';

export function isMonthlyAnnexure11OverallOutcomeCard(cardIndex: number): boolean {
  return monthlyAnnexure11RowIdForCard(cardIndex) === MONTHLY_ANNEXURE_11_OVERALL_OUTCOME_ROW_ID;
}

/**
 * Ongoing monthly Annexure 11: Observations and Remarks on Overall Performance Outcome
 * must be filled before save or submit. Other indicators stay optional.
 */
export function monthlyAnnexure11OverallOutcomeRequiredMessage(
  response: Record<string, unknown>,
): string | null {
  const cardIndex = MONTHLY_ANNEXURE_11_ROW_IDS.indexOf(MONTHLY_ANNEXURE_11_OVERALL_OUTCOME_ROW_ID) + 1;
  if (cardIndex < 1) return null;
  const observations = getMonthlyAnnexure11FieldValue(response, cardIndex, 'observations').trim();
  const remarks = getMonthlyAnnexure11FieldValue(response, cardIndex, 'remarks').trim();
  if (observations && remarks) return null;
  return 'Overall Performance Outcome is required';
}

/** Read saved Annexure 11 values (designer / Excel use varied column ids per row). */
export function getMonthlyAnnexure11FieldValue(
  response: Record<string, unknown>,
  cardIndex: number,
  field: 'observations' | 'remarks',
): string {
  const rowId = monthlyAnnexure11RowIdForCard(cardIndex);
  const cols =
    field === 'observations' ? MONTHLY_ANNEXURE_11_OBSERVATION_COLUMN_IDS : MONTHLY_ANNEXURE_11_REMARKS_COLUMN_IDS;
  for (const col of cols) {
    const v = getResponseValue(response, `${rowId}_${col}`);
    if (v.trim()) return v;
  }
  return '';
}

export function patchMonthlyAnnexure11Field(
  response: Record<string, unknown>,
  cardIndex: number,
  field: 'observations' | 'remarks',
  value: string,
): Record<string, unknown> {
  const rowId = monthlyAnnexure11RowIdForCard(cardIndex);
  const key = field === 'observations' ? `${rowId}_observations` : `${rowId}_remarks_status`;
  return patchResponseValue(response, key, value);
}

/** Excel layout for ongoing monthly Annexure 11 — matches the on-screen card grid (not admin table columns). */
export function annexure11OngoingMonthlyExcelDefinition(
  definition: AnnexureDefinitionPayload
): AnnexureDefinitionPayload {
  return {
    ...definition,
    tables: [
      {
        sectionTitle: 'Performance Indicators',
        columns: [
          { id: 'indicator', label: 'Performance Indicator', type: 'display' },
          { id: 'observations', label: 'Observations', type: 'text' },
          { id: 'remarks_status', label: 'Remarks', type: 'text' },
        ],
        rows: MONTHLY_ANNEXURE_11_ROW_IDS.map((id, index) => ({
          id,
          cells: {
            indicator: MONTHLY_PERFORMANCE_INDICATORS[index] ?? '',
          },
        })),
      },
    ],
  };
}

/** Map card-based saved values into the simplified Excel column keys before export. */
export function flattenAnnexure11ResponseForOngoingExcel(
  response: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!response) return response;
  const next = { ...response };
  for (let i = 0; i < MONTHLY_ANNEXURE_11_ROW_IDS.length; i += 1) {
    const rowId = MONTHLY_ANNEXURE_11_ROW_IDS[i];
    const cardIndex = i + 1;
    const obs = getMonthlyAnnexure11FieldValue(response, cardIndex, 'observations');
    const remarks = getMonthlyAnnexure11FieldValue(response, cardIndex, 'remarks');
    next[`${rowId}_observations`] = obs;
    next[`${rowId}_remarks_status`] = remarks;
  }
  return next;
}

const ANNEXURE_11_LEGACY_RESPONSE_SUFFIXES = [
  'assessment',
  'kyc_applicable',
  'sample_checking',
  'overall_performance',
  'action_taken',
] as const;

function normalizeAnnexure11IndicatorLabel(label: string): string {
  return label.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Match indicator label from Excel column B when hidden row id (column A) is missing. */
export function annexure11RowIdFromIndicatorLabel(label: string): string | null {
  const normalized = normalizeAnnexure11IndicatorLabel(label);
  if (!normalized) return null;
  let idx = MONTHLY_PERFORMANCE_INDICATORS.findIndex(
    (l) => normalizeAnnexure11IndicatorLabel(l) === normalized
  );
  if (idx < 0) {
    idx = MONTHLY_PERFORMANCE_INDICATORS.findIndex((l) => {
      const candidate = normalizeAnnexure11IndicatorLabel(l);
      return candidate.startsWith(normalized) || normalized.startsWith(candidate);
    });
  }
  if (idx < 0) return null;
  return MONTHLY_ANNEXURE_11_ROW_IDS[idx] ?? null;
}

/** True when the Excel grid is the 17-row monthly Annexure 11 card layout. */
export function isAnnexure11MonthlyExcelGrid(allowedRowIds: ReadonlySet<string>): boolean {
  if (allowedRowIds.size !== MONTHLY_ANNEXURE_11_ROW_IDS.length) return false;
  return MONTHLY_ANNEXURE_11_ROW_IDS.every((id) => allowedRowIds.has(id));
}

/** Fallback row id by 0-based order in the Performance Indicators table (last row = index 16). */
export function annexure11RowIdByImportSequence(sequenceIndex: number): string | null {
  return MONTHLY_ANNEXURE_11_ROW_IDS[sequenceIndex] ?? null;
}

/** Clear observations that were wrongly saved as the performance-indicator label (legacy import bug). */
export function repairAnnexure11MisimportedObservations(
  response: Record<string, unknown>
): Record<string, unknown> {
  let next = { ...response };
  for (let i = 0; i < MONTHLY_ANNEXURE_11_ROW_IDS.length; i += 1) {
    const rowId = MONTHLY_ANNEXURE_11_ROW_IDS[i];
    const label = MONTHLY_PERFORMANCE_INDICATORS[i] ?? '';
    const key = `${rowId}_observations`;
    if (getResponseValue(next, key) === label) {
      next = patchResponseValue(next, key, '');
    }
  }
  return next;
}

/**
 * After Excel import (or when loading saved data): canonicalize keys and drop legacy admin columns.
 */
export function normalizeAnnexure11ResponseAfterExcelImport(
  response: Record<string, unknown>
): Record<string, unknown> {
  const source = repairAnnexure11MisimportedObservations(response);
  let next = { ...source };
  for (let cardIndex = 1; cardIndex <= MONTHLY_ANNEXURE_11_ROW_IDS.length; cardIndex += 1) {
    const rowId = monthlyAnnexure11RowIdForCard(cardIndex);
    const observations = getMonthlyAnnexure11FieldValue(source, cardIndex, 'observations');
    const remarks = getMonthlyAnnexure11FieldValue(source, cardIndex, 'remarks');
    next = patchMonthlyAnnexure11Field(next, cardIndex, 'observations', observations);
    next = patchMonthlyAnnexure11Field(next, cardIndex, 'remarks', remarks);
    for (const suffix of ANNEXURE_11_LEGACY_RESPONSE_SUFFIXES) {
      delete next[`${rowId}_${suffix}`];
    }
  }
  delete next.n11_new_3_assessment;
  delete next.n11_new_3_observations;
  delete next.n11_new_3_action_taken;
  delete next.n11_new_3_remarks_status;
  delete next.n11_new_3_kyc_applicable;
  return next;
}
